import { useQueries, useQueryClient } from '@tanstack/react-query';
import mqtt from 'mqtt';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { pscDashboardApi } from '@/domains/traffic-monitoring/apis/psc-dashboard';
import { smartCityApi } from '@/domains/traffic-monitoring/apis/smart-city';
import { StatusAmbulanceDriver } from '@/domains/traffic-monitoring/constants/listEvent.constant';
import {
  overlayLiveAmbulances,
  overlayLiveRoutePoints,
  useAmbulanceLivePositions,
} from '@/domains/traffic-monitoring/hooks/useAmbulanceLivePositions';
import type {
  AmbulanceCardView,
  AmbulanceDriverType,
  AmbulanceListItem,
  AmbulanceRouteData,
  AmbulanceStatusCode,
  CardEventPscView,
  FilterItemView,
  MapMarkerView,
  PolylineView,
  ReportEventType,
  ReportTodayItem,
  TabItemView,
} from '@/domains/traffic-monitoring/types/psc-dashboard.types';
import type { MapPoiData } from '@/domains/traffic-monitoring/types/smart-city.types';
import { URLS } from '@/shared/config/urls';
import { useAuthStore } from '@/store/authStore';
import { NEWCOLOR_PALETTE } from '@/store/colorStore';

const DASHBOARD_TOPIC = 'psc-dashboard-updates';

export interface UsePscDashboardParams {
  refetchIntervalMs?: number;
}

// Exported so PSCDashboard.tsx's Active/Past tabs split cardData the same way
// deriveTabs counts it below.
//
// 'finished' is deliberately NOT terminal: the ambulance side is done, but the
// report only moves to Past once an operator explicitly clicks "Complete
// Report" (event_type becomes 'completed') - see ReportDetails.tsx /
// post_report_complete.py. Until then it stays Active so it doesn't get lost.
export const TERMINAL_EVENT_TYPES: ReportEventType[] = ['rejected', 'completed'];

const EVENT_TYPE_TO_TYPE_CARD: Record<ReportEventType, 'header' | 'default'> = {
  incoming: 'header',
  registered: 'header',
  dispatched: 'header',
  arrived: 'default',
  patient_handled: 'default',
  transfer: 'default',
  arrived_at_hospital: 'default',
  finished: 'default',
  rejected: 'default',
  completed: 'default',
};

const EVENT_TYPE_TO_TEXT_BUTTON: Record<ReportEventType, string> = {
  incoming: 'register & dispatch',
  registered: 'dispatch',
  dispatched: 'edit',
  arrived: 'edit',
  patient_handled: 'edit',
  transfer: 'edit',
  arrived_at_hospital: 'edit',
  finished: 'complete',
  rejected: 'detail',
  completed: 'see-details',
};

const AMB_STATUS_TO_DRIVER_TYPE: Record<AmbulanceStatusCode, AmbulanceDriverType> = {
  AMB_ST_ONGO: 'ongoing',
  AMB_ST_STBY: 'standby',
  AMB_ST_MNT: 'maintenance',
  AMB_ST_OFF: 'offline',
  // This fleet-monitoring panel intentionally keeps the simpler 4-value
  // status - a vehicle with a pending, not-yet-accepted request still reads
  // as Standby here. The Ambulance Dispatch picker (useAmbulanceDispatch.ts)
  // is where "Requested" actually needs to show, so an operator doesn't pick
  // an already-spoken-for vehicle.
  AMB_ST_REQ: 'standby',
};

// Standby's map pin is two-tone (light mint background, green icon) per
// design, rather than the single-tone status-color-background/white-icon
// pin every other status uses below.
const STANDBY_PIN_BACKGROUND = '#D1FEEF';

// Same status → color resolution AmbulanceCard.tsx/AmbulanceList.tsx use for
// the fleet panel (StatusAmbulanceDriver + NEWCOLOR), so an ambulance's map
// pin always matches its row in that right-hand list.
const ambulanceStatusColors = (
  statusCode: string | null | undefined
): { background: string; icon: string } => {
  const driverType = AMB_STATUS_TO_DRIVER_TYPE[statusCode as AmbulanceStatusCode] ?? 'offline';
  if (driverType === 'standby') {
    return { background: STANDBY_PIN_BACKGROUND, icon: NEWCOLOR_PALETTE.greenPrimary };
  }
  return { background: NEWCOLOR_PALETTE[StatusAmbulanceDriver[driverType].color], icon: '#FFFFFF' };
};

// --------------------------------------------------------------------------- //
// Normalizers — the backend already speaks the frontend's eventTypes/
// ambulanceTypes vocabulary, so these are renames + presentation-only lookups,
// not business-logic derivation. See CLAUDE.md.
// --------------------------------------------------------------------------- //

// endIso omitted -> now, so an active report's Duration keeps ticking live.
const minutesBetween = (startIso: string | null, endIso: string | null): number => {
  if (!startIso) return 0;
  const endMs = endIso ? new Date(endIso).getTime() : Date.now();
  const elapsedMs = endMs - new Date(startIso).getTime();
  return Math.max(0, Math.floor(elapsedMs / 60000));
};

// The report's own close stamp for each terminal event_type -
// finished_operator_datetime (reject) / report_handling_completion_time
// (complete) - NOT last_activity_time: Report Details stays editable after
// closure (see CLAUDE.md), and an edit there only bumps updated_time, which
// last_activity_time is a GREATEST() over. Using that would have let Duration
// drift upward again on any post-closure correction. These two columns are
// stamped once, at the actual close, and never move afterward.
const reportClosedAt = (report: ReportTodayItem): string | null => {
  if (report.event_type === 'rejected') return report.finished_operator_datetime;
  if (report.event_type === 'completed') return report.report_handling_completion_time;
  return null;
};

const toCardView = (report: ReportTodayItem): CardEventPscView => ({
  reportId: report.report_id,
  typeCard: EVENT_TYPE_TO_TYPE_CARD[report.event_type],
  eventTypes: report.event_type,
  eventTypeLabel: report.event_type_label,
  action: 'action request',
  report_code_id: report.report_code_id || '',
  // Intake channel (EXT_ID_TP_*) controls CardEventPsc's channel icon.
  // Passed through untouched; cm_code stays the vocabulary owner.
  extensionId: report.extension_id ?? null,
  address: report.incident_location || '',
  registeredTime: report.registered_time,
  lastActivityTime: report.last_activity_time,
  lastActivityAmbulance: report.last_activity_ambulance,
  timer: minutesBetween(report.registered_time, reportClosedAt(report)),
  // event_type stays "registered" for three different real situations (see
  // get_reports_today.py's _derive_event_type/_shape_event_fields), and each
  // needs its own button:
  //   - a rejected slot needing a replacement -> 'redispatch' (picker)
  //   - no rejection, but at least one ambulance already requested/accepted
  //     (a partially-filled multi-victim dispatch, or one not yet answered) ->
  //     'edit' (details) - a dispatch is already in flight, re-opening the
  //     picker would look like starting a new one instead of checking status
  //   - nothing dispatched at all yet -> 'dispatch' (picker), first time
  textButton:
    report.event_type === 'registered'
      ? report.ambulance_reports.some((entry) => entry.ambulance_type === 'rejected')
        ? 'redispatch'
        : report.ambulance_reports.length > 0
          ? 'edit'
          : 'dispatch'
      : EVENT_TYPE_TO_TEXT_BUTTON[report.event_type],
  // Every report still shows its button (mapped per event_type by
  // EVENT_TYPE_TO_TEXT_BUTTON), routing to the report detail screen so even
  // a rejected/completed report stays reachable from this list.
  showButton: true,
  showAmbulance: report.ambulance_reports.length > 0,
  ambulances: report.ambulance_reports.map((entry) => ({
    platNumber: entry.police_number || '',
    ambulanceTypes: entry.ambulance_type,
    statusLabel: entry.ambulance_type_label,
  })),
  totalNotif: report.unread_count,
});

const deriveTabs = (cards: CardEventPscView[]): TabItemView[] => {
  const activeCount = cards.filter((c) => !TERMINAL_EVENT_TYPES.includes(c.eventTypes)).length;
  return [
    { id: 'active', label: 'Active', count: activeCount },
    { id: 'past', label: 'Past', count: cards.length - activeCount },
  ];
};

const AMBULANCE_PAGE_SIZE = 100;

// ambulance/list orders by status (cm_code.sort_order) before ambulance_id, and
// caps size at 100 server-side - a single page can be 100% one status (seen live:
// 134-vehicle fleet, page 1 was 100% standby, hiding every on-going/maintenance/
// offline vehicle entirely). Fetch every page so no status silently disappears.
const fetchAllAmbulances = async (): Promise<AmbulanceListItem[]> => {
  // Server-enforced regardless (get_ambulance_list.py ignores/overrides this
  // for a healthcare account), but sent anyway so this account's own view
  // reflects its scope from the first page rather than relying on the 200.
  const { account_type, health_care_facility_id } = useAuthStore.getState();
  const facilityFilter =
    account_type === 'healthcare' && health_care_facility_id != null
      ? { health_care_facility_id }
      : {};

  const first = await pscDashboardApi.getAmbulanceList({
    page: 1,
    size: AMBULANCE_PAGE_SIZE,
    ...facilityFilter,
  });
  const items = first?.data?.list ?? [];
  const total = first?.data?.total_count ?? items.length;
  if (items.length >= total) return items;

  const remainingPages = Math.ceil((total - items.length) / AMBULANCE_PAGE_SIZE);
  const rest = await Promise.all(
    Array.from({ length: remainingPages }, (_, i) =>
      pscDashboardApi.getAmbulanceList({
        page: i + 2,
        size: AMBULANCE_PAGE_SIZE,
        ...facilityFilter,
      })
    )
  );
  return [...items, ...rest.flatMap((res) => res?.data?.list ?? [])];
};

const toAmbulanceView = (item: AmbulanceListItem): AmbulanceCardView => ({
  id: item.ambulance_id,
  platNumber: item.police_number || '',
  ambulanceDriverTypes: AMB_STATUS_TO_DRIVER_TYPE[item.ambulance_status] ?? 'offline',
  address: item.health_care_facility?.name || '',
  healthCareFacilityId: item.health_care_facility?.health_care_facility_id ?? null,
});

const deriveFilterCounts = (items: AmbulanceCardView[]): FilterItemView[] => {
  const counts: Record<AmbulanceDriverType, number> = {
    ongoing: 0,
    standby: 0,
    maintenance: 0,
    offline: 0,
  };
  for (const item of items) counts[item.ambulanceDriverTypes] += 1;
  return [
    { id: 'all', label: 'All', total: items.length },
    { id: 'standby', label: 'Standby', total: counts.standby },
    { id: 'maintenance', label: 'Maintenance', total: counts.maintenance },
    { id: 'ongoing', label: 'On-Going', total: counts.ongoing },
    { id: 'offline', label: 'Offline', total: counts.offline },
  ];
};

// Reuses the exact marker `type` keys useSmartCityDashboard.ts already picks
// for this same source, for consistency across the app.
const toMapMarkers = (data: MapPoiData | null): MapMarkerView[] => {
  if (!data?.layers) return [];
  const markers: MapMarkerView[] = [];

  for (const f of data.layers.health_facility ?? []) {
    if (f.position?.lat == null || f.position?.lon == null) continue;
    markers.push({
      id: `health-${f.id}`,
      lat: f.position.lat,
      lng: f.position.lon,
      type: 'healthcarepoi',
      tooltip: { title: f.name || 'Healthcare', description: f.address || '' },
      facilityId: f.id,
      facilityType: f.type?.code || undefined,
    });
  }

  for (const a of data.layers.ambulance ?? []) {
    if (a.position?.lat == null || a.position?.lon == null) continue;
    const { background, icon } = ambulanceStatusColors(a.status?.code);
    const driverType =
      AMB_STATUS_TO_DRIVER_TYPE[a.status?.code as AmbulanceStatusCode] ?? 'offline';
    markers.push({
      id: `ambulance-${a.id}`,
      lat: a.position.lat,
      lng: a.position.lon,
      type: 'ambulancepoi',
      tooltip: { title: a.police_number || 'Ambulance', description: a.status?.label || '' },
      color: background,
      iconColor: icon,
      isOffline: driverType === 'offline',
      subtitle: a.home_facility?.name || undefined,
    });
  }

  for (const i of data.layers.incident ?? []) {
    if (i.position?.lat == null || i.position?.lon == null) continue;
    markers.push({
      id: `incident-${i.id}`,
      lat: i.position.lat,
      lng: i.position.lon,
      type: 'Incident',
      tooltip: { title: i.report_code_id || 'Incident', description: i.incident_location || '' },
      // map/poi's incident.id is the report_id (see get_map_poi.py) - same id
      // PSCDashboard.tsx's report cards use for this marker's `incident-` key.
      reportId: i.id,
    });
  }

  return markers;
};

// One Polyline per GeoJSON segment - MultiLineString becomes N polylines,
// since SmartCityMaps' Polyline type only carries a single flat point list.
const toPolylines = (data: AmbulanceRouteData | null): PolylineView[] => {
  if (!data?.features) return [];
  const polylines: PolylineView[] = [];

  for (const feature of data.features) {
    const segments =
      feature.geometry.type === 'MultiLineString'
        ? (feature.geometry.coordinates as number[][][])
        : [feature.geometry.coordinates as number[][]];

    segments.forEach((points, index) => {
      polylines.push({
        id: `route-${feature.id}-${index}`,
        ambulanceId: feature.properties.ambulance_id,
        color: '#00A2FF',
        width: 4,
        points,
      });
    });
  }

  return polylines;
};

export const usePscDashboard = ({ refetchIntervalMs = 2000 }: UsePscDashboardParams = {}) => {
  const queryClient = useQueryClient();
  // event_type_label / status_label / POI tooltips are localized server-side
  // from the caller's language_preference, so the language belongs in the query
  // key - otherwise those labels keep the old language until the next interval.
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // PSC_MA0302001_BE_004: live GPS beacons over MQTT (retained topics). The
  // POI poll below stays as the base/fallback layer; MQTT moves the
  // ambulance markers in between polls so the fleet visibly drives around.
  const livePositions = useAmbulanceLivePositions();

  useEffect(() => {
    const client = mqtt.connect(URLS.MQTT_DSH, { connectTimeout: 5000, keepalive: 15 });
    client.on('connect', () => client.subscribe(DASHBOARD_TOPIC));
    client.on('message', (topic) => {
      if (topic !== DASHBOARD_TOPIC) return;
      queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
    });
    return () => {
      client.end(true);
    };
  }, [queryClient]);

  return useQueries({
    queries: [
      // 1. Reports Today
      {
        queryKey: ['pscReportsToday', lang],
        queryFn: async () => {
          const res = await pscDashboardApi.getReportsToday();
          return (res?.data ?? []).map(toCardView);
        },
        refetchInterval: refetchIntervalMs * 2,
        staleTime: 15000,
      },
      // 2. Ambulance Roster
      {
        queryKey: ['pscAmbulanceList'],
        queryFn: async () => {
          const items = await fetchAllAmbulances();
          return items.map(toAmbulanceView);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
      // 3. Map POIs
      {
        queryKey: ['pscMapPoi', lang],
        queryFn: async () => {
          const res = await smartCityApi.getMapPoi({
            layers: 'health_facility,incident,ambulance',
            // Default incident_scope ('open') drops completed/rejected reports,
            // so a Past-tab report card would have no marker to focus/blink -
            // 'today' keeps every incident from today's report list (same
            // window get_reports_today.py uses), matching SmartCityDashboard.
            incident_scope: 'today',
          });
          return toMapMarkers(res?.data ?? null);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
      // 4. Active Ambulance Routes
      {
        queryKey: ['pscAmbulanceRoutes'],
        queryFn: async () => {
          const res = await pscDashboardApi.getAmbulanceRouteList();
          return toPolylines(res?.data ?? null);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
    ],
    combine: ([reportsQuery, ambulanceQuery, mapQuery, routesQuery]) => {
      const cardData = (reportsQuery.data as CardEventPscView[]) || [];
      const ambulanceData = (ambulanceQuery.data as AmbulanceCardView[]) || [];

      return {
        cardData,
        tabsFilter: deriveTabs(cardData),
        ambulanceData,
        filterData: deriveFilterCounts(ambulanceData),
        // Live MQTT positions win over the polled snapshot marker-by-marker.
        // The roster gates which ids may appear at all (anonymous broker).
        markers: overlayLiveAmbulances(
          (mapQuery.data as MapMarkerView[]) || [],
          livePositions,
          new Set(ambulanceData.map((item) => item.id))
        ),
        polylines: overlayLiveRoutePoints(
          (routesQuery.data as PolylineView[]) || [],
          livePositions
        ),

        isPending:
          reportsQuery.isPending ||
          ambulanceQuery.isPending ||
          mapQuery.isPending ||
          routesQuery.isPending,

        isError:
          reportsQuery.isError || ambulanceQuery.isError || mapQuery.isError || routesQuery.isError,
      };
    },
  });
};
